import { createReducer, on } from '@ngrx/store';
import { PaymentActions } from '@store/actions/payment.actions';

export type PaymentConfigStatus = 'idle' | 'loading' | 'ready' | 'error';
export type HasPaidStatus      = 'idle' | 'loading' | 'checked';
export type VerifyStatus       = 'idle' | 'polling' | 'confirmed' | 'not_found' | 'no_session' | 'error';

export interface PaymentState {
  // Paddle config from /api/payment-config
  configStatus: PaymentConfigStatus;
  configError:  string | null;
  clientToken:  string | null;
  priceId:      string | null;
  isSandbox:    boolean;

  // Whether the logged-in user has an active purchase (pricing page)
  hasPaidStatus: HasPaidStatus;
  hasPaid:       boolean;

  // Post-checkout verification status (/payment page)
  verifyStatus: VerifyStatus;
  verifyError:  string | null;
}

const initialState: PaymentState = {
  configStatus: 'idle',
  configError:  null,
  clientToken:  null,
  priceId:      null,
  isSandbox:    false,

  hasPaidStatus: 'idle',
  hasPaid:       false,

  verifyStatus: 'idle',
  verifyError:  null,
};

export const paymentReducer = createReducer(
  initialState,

  // ── Config ────────────────────────────────────────────────────
  on(PaymentActions.loadConfig, state => ({
    ...state, configStatus: 'loading' as const, configError: null,
  })),
  on(PaymentActions.loadConfigSuccess, (state, { clientToken, priceId, isSandbox }) => ({
    ...state,
    configStatus: 'ready' as const,
    configError:  null,
    clientToken,
    priceId,
    isSandbox,
  })),
  on(PaymentActions.loadConfigFailure, (state, { error }) => ({
    ...state, configStatus: 'error' as const, configError: error,
  })),

  // ── Has Paid ──────────────────────────────────────────────────
  on(PaymentActions.checkHasPaid, state => ({
    ...state, hasPaidStatus: 'loading' as const,
  })),
  on(PaymentActions.checkHasPaidSuccess, (state, { hasPaid }) => ({
    ...state, hasPaidStatus: 'checked' as const, hasPaid,
  })),
  on(PaymentActions.checkHasPaidFailure, state => ({
    // Treat query errors as not-paid (safe fallback - user can retry)
    ...state, hasPaidStatus: 'checked' as const, hasPaid: false,
  })),

  // ── Verify Payment ────────────────────────────────────────────
  on(PaymentActions.verifyPayment, state => ({
    ...state, verifyStatus: 'polling' as const, verifyError: null,
  })),
  on(PaymentActions.verifyPaymentSuccess, state => ({
    ...state, verifyStatus: 'confirmed' as const,
  })),
  on(PaymentActions.verifyPaymentNotFound, state => ({
    ...state, verifyStatus: 'not_found' as const,
  })),
  on(PaymentActions.verifyPaymentNoSession, state => ({
    ...state, verifyStatus: 'no_session' as const,
  })),
  on(PaymentActions.verifyPaymentFailure, (state, { error }) => ({
    ...state, verifyStatus: 'error' as const, verifyError: error,
  })),
);
